import type { Vec2 } from '../../geo/types';
import type { LifePose, LifeRoute } from './types';
import { distance } from './streetGraph';

/** One of the cast as it walks: where its itinerary has it, and what it has made of that. */
export interface Walker {
  clock: number;
  offset: Vec2;
  pose: LifePose;
  walked: number;
  /** Metres a second over the ground: gathered and lost, never taken or dropped in a frame. */
  speed: number;
  /** The share of its stride it is drawn in. */
  gait: number;
  /** The side it stepped out to, and how long it keeps to it after the way is clear. */
  side: number;
  aside: number;
  /** What it last made up its mind to do, how much longer it keeps to that, and whether
   *  someone was in its way when it did. */
  choice: { readonly kind: 'go' | 'shift' | 'stand'; readonly side: number };
  decided: number;
  wary: boolean;
  /** How long it has made next to no way, and how much longer it goes on through whoever is
   *  in it. */
  thwarted: number;
  squeezing: number;
}

/** How the cast gathers and loses its pace. Every number is an estimate of what reads as walking. */
export const PACE = {
  /** Seconds from a stand to its full pace; it brakes for someone in its way twice as hard. */
  rampS: 0.45,
  /** It begins to slow this long before its walk ends, and arrives at this share of its pace. */
  stopS: 0.5,
  arriving: 0.2,
  /** Walking within the first angle of the way it faces costs no pace; at the second it stands
   *  and turns before it goes. */
  freeTurn: (20 * Math.PI) / 180,
  standTurn: (70 * Math.PI) / 180,
  /** It looks this far along its way, so its body is into a bend before its feet are, by no
   *  more than this angle. */
  lookS: 0.35,
  lean: (15 * Math.PI) / 180,
  /** Its stride is whole from this share of its pace, and settles over this long. */
  striding: 0.5,
  gaitS: 0.25,
  /** It keeps to the side it stepped out to for this long after nobody is in its way. */
  asideS: 0.8,
  /** Of each step it takes while stepping aside, this share goes sideways. */
  sideways: 0.6,
  /** It looks this far before it for someone to slow behind, and follows no nearer than the
   *  second. */
  headwayM: 3,
  followM: 0.3,
  /** It is back on its own line this long before it comes to a door. */
  doorS: 3,
  /** Stopping within this of its place, it settles onto it. */
  settleM: 0.06,
  /** Slower than the first, metres a second, is standing behind someone and not following
   *  them. Below the second it is drawn standing. */
  crawl: 0.12,
  still: 0.005,
  /** It keeps to what it made up its mind to do for this long. One that chose afresh in every
   *  frame stepped into a gap and back out of it sixty times a second. */
  decideS: 0.4,
  /** Blocked within this many seconds' walk of where it was to stop, it has arrived. */
  nearS: 2,
  /** The ground beside its line is asked about a step of at least this length, and a way is
   *  taken only with this much of it free of people. Within the third of its line it is on it:
   *  a cloister walk is cleared for the line and for nothing beside it. */
  probeM: 0.1,
  roomM: 0.25,
  onLineM: 0.03,
  /** Two who meet where neither can step aside, in a doorway or a cloister walk, would stand
   *  face to face for good. One that has made less than the first share of its way for the
   *  second goes on through whoever is in it for the third. The second is longer than the
   *  night watch takes to sort itself out at the Stadhuis, which it does without this. */
  thwarting: 0.25,
  thwartedS: 36,
  squeezeS: 2.5,
  /** Two within this of one another, neither making way, are in each other's. */
  standoffM: 1.3,
} as const;

export const toward = (from: number, to: number, step: number): number =>
  from + Math.max(-step, Math.min(step, to - from));
export const turnBetween = (from: number, to: number): number =>
  Math.abs(Math.atan2(Math.sin(to - from), Math.cos(to - from)));
export const bearing = (from: Vec2, to: Vec2): number =>
  Math.atan2(to[1] - from[1], to[0] - from[0]);
export const shifted = (p: Vec2, heading: number, side: number): Vec2 => [
  p[0] + Math.sin(heading) * side,
  p[1] - Math.cos(heading) * side,
];
export const approach = (from: Vec2, to: Vec2, step: number): Vec2 => {
  const length = distance(from, to);
  const t = length ? Math.min(1, step / length) : 1;
  return [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t];
};
export const offsetAt = (p: Vec2, offset: Vec2): Vec2 => [p[0] + offset[0], p[1] + offset[1]];
export const bodyRadius = (route: LifeRoute) =>
  route.role === 'horse' ? 1.2 : route.role === 'dog' ? 0.23 : 0.34;
